import React from "react";

/**
 * Devuelve un ref que, cuando `isSelected` pasa a true, hace scroll del elemento
 * hasta el tope del viewport.
 *
 * Mantiene el scroll suave (la UX deseada al tocar una categoría). Pero en un
 * deep-link recién abierto (p. ej. al tocar el link desde WhatsApp/Slack) la
 * página todavía está cargando: las imágenes/fuentes de arriba mueven el target
 * y algunos WebView interrumpen la animación o resetean el scroll al top en el
 * evento "load". Por eso, si la página aún no terminó de cargar, corregimos el
 * scroll de forma instantánea cuando termina (y con un timeout de respaldo) para
 * garantizar que el deep-link quede posicionado en la categoría.
 */
export function useScrollOnSelect<T extends HTMLElement = HTMLElement>(isSelected: boolean) {
  const element = React.useRef<T>(null);

  React.useEffect(() => {
    if (!isSelected) return;

    const scroll = (behavior: ScrollBehavior) =>
      element.current?.scrollIntoView({behavior, block: "start", inline: "start"});

    const raf = requestAnimationFrame(() => scroll("smooth"));

    // Página ya estable (caso típico: click del usuario) → alcanza el scroll suave.
    if (typeof document === "undefined" || document.readyState === "complete") {
      return () => cancelAnimationFrame(raf);
    }

    const correct = () => scroll("auto");

    window.addEventListener("load", correct);
    const timer = window.setTimeout(correct, 1200);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      window.removeEventListener("load", correct);
    };
  }, [isSelected]);

  return element;
}
